import { apiErrorMessage, useAssignCompanies } from "@vidario/api-client";
import { employerRef, undoBatches, type EmployerGroup } from "@vidario/domain";
import { useToast } from "@vidario/ui";

export interface AssignOptions {
  rows: readonly EmployerGroup[];
  /** Target class, or null to put the rows back onto the to-do list. */
  classId: number | null;
  /** E.g. „„KPMG" → „Big Four" zugeordnet". */
  message: string;
  onDone?: () => void;
}

/** Assigns rows and offers „Rückgängig", which puts every row back into the
 * class it had before — the whole batch. */
export function useAssignWithUndo(announce: (text: string) => void): {
  assign: (options: AssignOptions) => void;
  pending: boolean;
} {
  const mutation = useAssignCompanies();
  const { push } = useToast();

  // A class deleted or a company merged meanwhile can make part of the undo
  // fail; say how much went back instead of a bare failure.
  const undo = (rows: readonly EmployerGroup[]): void => {
    const batches = undoBatches(rows);
    void Promise.allSettled(batches.map((batch) => mutation.mutateAsync(batch))).then((results) => {
      const failed = results.filter((r): r is PromiseRejectedResult => r.status === "rejected");
      if (failed.length === 0) {
        push({ kind: "success", message: "Zuordnung rückgängig gemacht" });
        announce("Zuordnung rückgängig gemacht.");
      } else if (failed.length < results.length) {
        push({
          kind: "warning",
          title: "Nur teilweise rückgängig gemacht",
          message: `Einige Firmen wurden inzwischen geändert. ${apiErrorMessage(failed[0]?.reason)}`,
        });
      } else {
        push({ kind: "error", title: "Rückgängig fehlgeschlagen", message: apiErrorMessage(failed[0]?.reason) });
      }
    });
  };

  const assign = ({ rows, classId, message, onDone }: AssignOptions): void => {
    const snapshot = [...rows];
    mutation.mutate(
      { items: snapshot.map(employerRef), classId },
      {
        onSuccess: () => {
          push({ kind: "success", message, action: { label: "Rückgängig", onClick: () => undo(snapshot) } });
          announce(`${message}.`);
          onDone?.();
        },
        onError: (err) => push({ kind: "error", title: "Zuordnung fehlgeschlagen", message: apiErrorMessage(err) }),
      },
    );
  };

  return { assign, pending: mutation.isPending };
}

/** „„KPMG" → „Big Four" zugeordnet" or „3 Firmen → „Big Four" zugeordnet". */
export function assignedMessage(rows: readonly EmployerGroup[], className: string): string {
  const subject = rows.length === 1 ? `„${rows[0]?.name ?? ""}“` : `${rows.length} Firmen`;
  return `${subject} → „${className}“ zugeordnet`;
}
